import { useEffect, useState, useCallback } from 'react'
import {
  Network, Factory, Split, Send, Building2, Power, Zap, Search, Eye, X, History, CheckCircle2,
} from 'lucide-react'
import api from '../api/client'
import Pagination from '../components/Pagination'
import Loader from '../components/Loader'
import SearchableSelect from '../components/SearchableSelect'
import { confirmModal, successModal } from '../components/ui'

// Fila de Conexão (operacional) — doc "Correção — Fluxo completo".
// Fluxo: Aprovado → Envio p/ Usina → Rateio em Proc. → Rateio Enviado →
//        Aguardando Concessionária → Em Ativação → Recebendo Créditos (Cliente Ativo).
const PASSOS = [
  { value: 'aprovado', label: 'Aprovado', icon: CheckCircle2 },
  { value: 'conexao_usina', label: 'Envio para a Usina', icon: Factory },
  { value: 'conexao_rateio_proc', label: 'Rateio em Processamento', icon: Split },
  { value: 'conexao_rateio_enviado', label: 'Rateio Enviado', icon: Send },
  { value: 'conexao_concessionaria', label: 'Aguardando Concessionária', icon: Building2 },
  { value: 'conexao_ativacao', label: 'Em Ativação', icon: Power },
  { value: 'ativo', label: 'Recebendo Créditos (Cliente Ativo)', icon: Zap },
]
const LABEL = Object.fromEntries(PASSOS.map((p) => [p.value, p.label]))
const IDX = Object.fromEntries(PASSOS.map((p, i) => [p.value, i]))
const OPERACIONAIS = PASSOS.slice(1) // sem 'aprovado' (entrada)

const CLS = {
  aprovado: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  conexao_usina: 'bg-sky-100 text-sky-800 border-sky-300',
  conexao_rateio_proc: 'bg-sky-100 text-sky-800 border-sky-300',
  conexao_rateio_enviado: 'bg-sky-100 text-sky-800 border-sky-300',
  conexao_concessionaria: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  conexao_ativacao: 'bg-violet-100 text-violet-800 border-violet-300',
  ativo: 'bg-emerald-100 text-emerald-800 border-emerald-300',
}
const fmtData = (v) => (v ? new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-')

function Chip({ status }) {
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${CLS[status] || 'bg-neutral-100 text-neutral-700'}`}>{LABEL[status] || status}</span>
}

/* ===== Modal de detalhe + avanço de status ===== */
function DetalheModal({ item, onClose, onDone }) {
  const [det, setDet] = useState(null)
  const [hist, setHist] = useState([])
  const [alvo, setAlvo] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    api.get(`/contratos-adesao/${item.id}`).then((r) => setDet(r.data)).catch(() => {})
    api.get(`/contratos-adesao/${item.id}/historico`).then((r) => setHist(r.data.items || [])).catch(() => {})
  }, [item.id])

  const d = det || item
  const atualIdx = IDX[d.status] ?? 0

  async function avancar(novo) {
    const nv = novo || alvo
    if (!nv) { setMsg('Selecione o próximo status'); return }
    const txt = nv === 'ativo'
      ? 'Marcar como Recebendo Créditos (Cliente Ativo)? Isso inicia a recorrência mensal.'
      : `Avançar para "${LABEL[nv]}"?`
    if (!(await confirmModal(txt, { title: 'Atualizar status', okLabel: 'Confirmar' }))) return
    setBusy(true)
    try {
      await api.post(`/contratos-adesao/${item.id}/status-conexao`, { status: nv })
      await successModal(`Status atualizado: ${LABEL[nv]}.`, { title: 'Feito!' })
      onDone()
    } catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao atualizar status') } finally { setBusy(false) }
  }

  const proximo = PASSOS[atualIdx + 1]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 bg-gradient-to-r from-primary-50 to-accent-50 border-b border-primary-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-graphite font-bold flex-shrink-0">
            {(d.cliente_nome || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-extrabold text-graphite truncate">{d.cliente_nome || 'Sem cliente'}</h3>
            <Chip status={d.status} />
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-graphite"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto tc-scroll space-y-5">
          {msg && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{msg}</p>}

          {/* Stepper visual */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            {PASSOS.map((p, i) => {
              const done = i <= atualIdx
              const Icon = p.icon
              return (
                <div key={p.value} className="flex items-center gap-1 flex-shrink-0">
                  <div className={`flex flex-col items-center gap-1 w-[86px] text-center`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${done ? 'bg-accent-500 text-white' : 'bg-neutral-100 text-neutral-400'}`}><Icon size={15} /></div>
                    <span className={`text-[9px] leading-tight ${done ? 'text-accent-700 font-semibold' : 'text-neutral-400'}`}>{p.label}</span>
                  </div>
                  {i < PASSOS.length - 1 && <div className={`h-0.5 w-3 ${i < atualIdx ? 'bg-accent-500' : 'bg-neutral-200'}`} />}
                </div>
              )
            })}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="CPF/CNPJ" value={d.cliente_cpf_cnpj} />
            <Info label="Distribuidora" value={d.distribuidora_nome || d.distribuidora?.nome} />
            <Info label="Gerador Parceiro" value={d.geradora_nome} />
            <Info label="Licenciado" value={d.licenciado_nome} />
            <Info label="Aprovado em" value={fmtData(d.aprovado_em)} />
            <Info label="Ativado em" value={fmtData(d.ativado_em)} />
          </div>

          {hist.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5"><History size={13} /> Histórico</p>
              <div className="space-y-1.5">
                {hist.map((h, i) => (
                  <div key={i} className="text-xs text-neutral-600 flex gap-2">
                    <span className="text-neutral-400 whitespace-nowrap">{fmtData(h.created_at)}</span>
                    <span>{h.mensagem || LABEL[h.status_novo] || h.acao}{h.usuario_nome ? <span className="text-neutral-400"> — {h.usuario_nome}</span> : null}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {d.status !== 'ativo' && (
          <div className="px-5 py-4 border-t border-neutral-100 bg-neutral-50 space-y-2">
            <div className="flex flex-wrap gap-2 items-center justify-end">
              <SearchableSelect value={alvo} onChange={setAlvo} emptyOption={false} className="w-60"
                options={OPERACIONAIS.map((p) => ({ value: p.value, label: p.label }))} placeholder="Definir status..." />
              <button onClick={() => avancar()} disabled={busy} className="px-4 py-2 rounded-xl border border-neutral-300 text-neutral-700 text-sm font-semibold hover:bg-neutral-100">Definir</button>
              {proximo && (
                <button onClick={() => avancar(proximo.value)} disabled={busy}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white text-sm font-bold shadow-md hover:brightness-95">
                  Avançar → {proximo.label}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
function Info({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</p>
      <p className="text-sm text-graphite font-medium">{value || '-'}</p>
    </div>
  )
}

export default function FilaConexao() {
  const [stats, setStats] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [pages, setPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [busca, setBusca] = useState('')
  const [debounced, setDebounced] = useState('')
  const [status, setStatus] = useState('todos')
  const [sel, setSel] = useState(null)

  useEffect(() => { const t = setTimeout(() => setDebounced(busca), 300); return () => clearTimeout(t) }, [busca])

  const loadStats = useCallback(() => { api.get('/contratos-adesao/_stats/conexao').then((r) => setStats(r.data)).catch(() => {}) }, [])
  const loadRows = useCallback(() => {
    setLoading(true)
    const base = { page, limit: size }
    if (debounced) base.search = debounced
    const fetch = status === 'todos'
      // "todos" = toda a fila de conexão (aprovado..ativo). Sem filtro nativo p/ conjunto → puxa por status.
      ? Promise.all(PASSOS.map((p) => api.get('/contratos-adesao', { params: { ...base, status: p.value, limit: 100 } }).then((r) => r.data.data || []).catch(() => [])))
          .then((arr) => { const all = arr.flat(); return { data: all.slice((page - 1) * size, page * size), total: all.length, pages: Math.max(1, Math.ceil(all.length / size)) } })
      : api.get('/contratos-adesao', { params: { ...base, status } }).then((r) => r.data)
    fetch.then((r) => { setRows(r.data || []); setPages(r.pages || 1); setTotal(r.total || 0) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }, [page, size, status, debounced])

  useEffect(() => { loadStats() }, [loadStats])
  useEffect(() => { loadRows() }, [loadRows])

  const filtrar = (s) => { setStatus(s); setPage(1) }
  const aoConcluir = () => { setSel(null); loadStats(); loadRows() }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-graphite flex items-center gap-2">
          <Network size={24} className="text-accent-500" /> Fila de Conexão
        </h1>
        <p className="text-sm text-neutral-500 mt-1">Evolução operacional das conexões aprovadas — até o cliente ativo (recebendo créditos)</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Aprovados (aguardando)" value={stats?.aprovado} active={status === 'aprovado'} onClick={() => filtrar('aprovado')} icon={CheckCircle2} color="emerald" />
        <Card label="Envio p/ Usina" value={stats?.usina} active={status === 'conexao_usina'} onClick={() => filtrar('conexao_usina')} icon={Factory} color="sky" />
        <Card label="Aguardando Concessionária" value={stats?.concessionaria} active={status === 'conexao_concessionaria'} onClick={() => filtrar('conexao_concessionaria')} icon={Building2} color="indigo" />
        <Card label="Clientes Ativos" value={stats?.ativos} active={status === 'ativo'} onClick={() => filtrar('ativo')} icon={Zap} color="emerald" />
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-3 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input value={busca} onChange={(e) => { setBusca(e.target.value); setPage(1) }} placeholder="Buscar por nome, CPF/CNPJ, UC ou licenciado..."
            className="w-full pl-10 pr-3 py-2 rounded-xl border border-neutral-200 text-sm focus:border-accent-400 focus:ring-2 focus:ring-accent-100 outline-none" />
        </div>
        <SearchableSelect value={status} onChange={(v) => { setStatus(v); setPage(1) }} emptyOption={false} className="w-60"
          options={[{ value: 'todos', label: 'Toda a fila de conexão' }, ...PASSOS.map((p) => ({ value: p.value, label: p.label }))]} />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader text="Carregando fila..." /></div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-primary-50 to-accent-50 border-b border-primary-100">
                  <th className="text-left px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider hidden md:table-cell">Distribuidora</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider hidden lg:table-cell">Licenciado</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-16 text-neutral-400">
                    <Network size={40} className="mx-auto mb-2 opacity-30" />
                    <p>Nenhuma conexão nesta fila</p>
                  </td></tr>
                ) : rows.map((v) => (
                  <tr key={v.id} onClick={() => setSel(v)} className="border-b border-neutral-100 hover:bg-primary-50/40 cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-graphite text-xs font-bold flex-shrink-0">{(v.cliente_nome || '?').charAt(0).toUpperCase()}</div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-graphite truncate">{v.cliente_nome || 'Sem cliente'}</p>
                          <p className="text-[11px] text-neutral-400 truncate">{v.cliente_cpf_cnpj || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-sm text-neutral-600">{v.distribuidora_nome || '-'}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-neutral-600">{v.licenciado_nome || '-'}</td>
                    <td className="px-4 py-3 text-center"><Chip status={v.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); setSel(v) }} className="p-2 rounded-lg hover:bg-accent-100 text-accent-600"><Eye size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <div className="px-4 py-3 border-t border-neutral-100">
              <Pagination page={page} pages={pages} total={total} pageSize={size} onPage={setPage} onPageSize={(n) => { setSize(n); setPage(1) }} />
            </div>
          )}
        </div>
      )}

      {sel && <DetalheModal item={sel} onClose={() => setSel(null)} onDone={aoConcluir} />}
    </div>
  )
}

function Card({ label, value, icon: Icon, color, active, onClick }) {
  const c = {
    emerald: { bg: 'bg-gradient-to-br from-emerald-500 to-emerald-600', ring: 'ring-emerald-300', text: 'text-emerald-600' },
    sky: { bg: 'bg-gradient-to-br from-sky-500 to-sky-600', ring: 'ring-sky-300', text: 'text-sky-600' },
    indigo: { bg: 'bg-gradient-to-br from-indigo-500 to-indigo-600', ring: 'ring-indigo-300', text: 'text-indigo-600' },
  }[color] || {}
  return (
    <button onClick={onClick}
      className={`bg-white rounded-2xl p-4 shadow-sm border border-neutral-200 text-left transition-all hover:-translate-y-0.5 ${active ? `ring-2 ${c.ring}` : 'hover:shadow-md'}`}>
      <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center shadow-sm mb-2`}><Icon size={18} className="text-white" strokeWidth={2.5} /></div>
      <p className="text-2xl font-heading font-extrabold text-graphite">{value ?? '-'}</p>
      <p className={`text-xs font-bold uppercase tracking-wider mt-0.5 ${c.text}`}>{label}</p>
    </button>
  )
}
